import { Email, Phone } from "@mui/icons-material"
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid"
import { deDE } from "@mui/x-data-grid/locales"
import { StyledButton } from "components/Button/StyledButton"
import { Contact } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { contactTypes } from "utils/constants"

type AddBusinessContactDialogProps = {
  open: boolean
  onClose: () => void
  businessAccount: string
  onSave: (contact: Contact, type: string) => void
}

export const AddBusinessContactDialog = (props: AddBusinessContactDialogProps) => {
  const { open, onClose, onSave, businessAccount } = props

  const { contacts } = useStoreState((state) => state)
  const { fetchContacts } = useStoreActions((action) => action)
  const [openContactDialog, setOpenContactDialog] = useState(false)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [selectedContactType, setSelectedContactType] = useState<string | null>(null)
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([])
  const [isFiltered, setIsFiltered] = useState(true)

  useEffect(() => {
    const loadContacts = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchContacts({ fresh: false })
      } catch (err) {
        console.error("Error loading contacts:", err)
        setError("Failed to load contacts. Please try again later.")
      } finally {
        setLoading(false)
      }
    }
    loadContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = async () => {
    try {
      setLoading(true)
      setError(null)
      await fetchContacts({ fresh: true })
    } catch (err) {
      console.error("Error reloading contacts:", err)
      setError("Failed to load contacts. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isFiltered) {
      const filteredContacts = contacts.filter((item) => item.businessAccount === businessAccount)
      setFilteredContacts(filteredContacts)
    } else {
      setFilteredContacts(contacts)
    }
  }, [contacts, businessAccount, isFiltered])

  const handleRowClick = (params: GridRowParams<Contact>) => {
    setSelectedContact(params.row)
    setOpenContactDialog(true)
  }

  const handleCloseContactDialog = () => {
    setOpenContactDialog(false)
    setSelectedContact(null)
  }

  const handleSaveContact = () => {
    if (selectedContact && selectedContactType) {
      onSave(selectedContact, selectedContactType)
    }
    setOpenContactDialog(false)
    onClose()
    setSelectedContact(null)
    setSelectedContactType(null)
  }

  const columns: GridColDef<Contact>[] = [
    {
      field: "displayName",
      headerName: "Name",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2">{params.row.displayName || "N/A"}</Typography>
        </Box>
      ),
    },
    {
      field: "jobTitle",
      headerName: "Position",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2">{params.row.jobTitle}</Typography>
        </Box>
      ),
    },
    {
      field: "companyName",
      headerName: "Unternehmen",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            height: "100%",
          }}
        >
          <Typography variant="body2">{params.row.companyName || "N/A"}</Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "E-Mail",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row.email && (
            <>
              <Email fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{params.row.email}</Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "phone1",
      headerName: "Telefon",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Contact>) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row.phone1 && (
            <>
              <Phone fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{params.row.phone1}</Typography>
            </>
          )}
        </Box>
      ),
    },
  ]

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="xl">
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h3">Kontakte hinzufügen</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Nur Kontakte dieses Kunden</Typography>
              <Switch
                checked={isFiltered}
                onChange={(event) => setIsFiltered(event.target.checked)}
                size="small"
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="error" variant="h6">
                {error}
              </Typography>
              <StyledButton onClick={handleRetry} sx={{ mt: 2 }}>
                Retry
              </StyledButton>
            </Box>
          ) : (
            <DataGrid
              getRowId={(row) => row.contactID || row.id}
              loading={loading}
              rows={filteredContacts}
              columns={columns}
              onRowClick={handleRowClick}
              sx={{
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                },
              }}
              slots={{
                toolbar: (props) => (
                  <GridToolbarContainer>
                    <GridToolbar />
                    <GridToolbarQuickFilter />
                  </GridToolbarContainer>
                ),
              }}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
            />
          )}
        </DialogContent>
        <DialogActions>
          <StyledButton buttonType="cancel" onClick={onClose} variant="contained" />
        </DialogActions>
      </Dialog>

      <Dialog open={openContactDialog}>
        <DialogTitle>Zu Projekt hinzufügen</DialogTitle>
        <DialogContent>
          <Typography variant="h6">{selectedContact?.displayName}</Typography>
          {selectedContact?.email && (
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Email /> {selectedContact?.email}
            </Typography>
          )}
          {selectedContact?.phone1 && (
            <Typography sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Phone />
              {selectedContact?.phone1}
            </Typography>
          )}
          <Autocomplete
            options={contactTypes}
            getOptionLabel={(option) => option}
            value={selectedContactType}
            onChange={(event, value) => {
              if (value) {
                setSelectedContactType(value)
              }
            }}
            renderInput={(params) => <TextField {...params} label="Kontaktart auswählen" />}
          />
        </DialogContent>
        <DialogActions>
          <StyledButton
            buttonType="cancel"
            onClick={handleCloseContactDialog}
            variant="contained"
          />
          <StyledButton
            buttonType="add"
            variant="contained"
            customText="Hinzufügen"
            onClick={handleSaveContact}
            disabled={!selectedContact || !selectedContactType}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}
